import { CircleCheckBig } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import {
  commitSwipe,
  dotState,
  initialOrder,
  markPiledUpSeen,
  piledUpBatchId,
  piledUpHeaderParts,
  readPiledUpSeen,
  reorderAfterSwipe,
  restOpacity,
  shouldPlayDeal,
  visibleBehind,
  type PiledUpJob,
  type SwipeResult,
} from '../../../lib/piled-up-jobs';
import { Card, Typography } from '../ui';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { PiledUpAvatar } from './PiledUpAvatar';
import styles from './PiledUpDeck.module.css';

// The mobile "piled up" section (Figma 9112:55884): a bold "N Jobs piled up…" header, a row of tri-state
// pagination dots, and a rotating horizontal swipe DECK. The front card is fully open (agent · workspace,
// task title, a check-circle); the ≤2 cards behind peek their avatars out the clipped left edge. Swiping
// the front card in either direction cycles it to the BACK and brings the next up (the deck never empties
// to one card) — pure browsing, no backend effect. All state lives in the shared pure logic in
// `lib/piled-up-jobs.ts`. On a first-seen batch the cards deal out; a return visit or reduce-motion shows
// the settled deck immediately.
//
// Matches what iOS/Android's `PiledUpSection` ship today: header + deck + dots only — no "Read all" /
// expanded view (the Figma frame draws a "Read all" control, but no surface implements it, so adding it
// here would be a web-only divergence; it stays a future coordinated three-surface change).

const PAGE = 'home';
/** Each card behind the front peeks this far left; the deepest visible card anchors flush at x=0. */
const PEEK = 16;
/** Drag distance (px) past which a release commits a swipe rather than snapping back. */
const SWIPE_THRESHOLD = 60;
/** Ignore a pointer-up as a swipe below this travel, so a tap still opens the task. */
const TAP_SLOP = 5;
/** Matches the fling transition in the stylesheet. */
const EXIT_MS = 320;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Exiting {
  deckIndex: number;
  dir: 1 | -1;
  startX: number;
  launched: boolean;
}

export function PiledUpDeck({ jobs }: { jobs: PiledUpJob[] }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openModal } = useTaskSidePanel();

  const deckCount = jobs.length;
  const multi = deckCount > 1;
  const vb = visibleBehind(deckCount);

  const [order, setOrder] = useState(() => initialOrder(deckCount));
  const [viewed, setViewed] = useState<Set<number>>(() => new Set());
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [exiting, setExiting] = useState<Exiting | null>(null);
  // Deal-out plays once per new batch (a return visit or reduce-motion shows the settled deck).
  const [deal] = useState(() => shouldPlayDeal(piledUpBatchId(jobs), readPiledUpSeen()) && !prefersReducedMotion());

  const startXRef = useRef(0);
  const movedRef = useRef(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-seat the deck when the batch itself changes (new set of jobs), and mark this batch seen so its
  // deal-out never replays.
  const batchId = piledUpBatchId(jobs);
  useEffect(() => {
    setOrder(initialOrder(deckCount));
    setViewed(new Set());
    setExiting(null);
    markPiledUpSeen(batchId);
  }, [batchId, deckCount]);
  useEffect(
    () => () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    },
    [],
  );

  const openJob = (job: PiledUpJob) => {
    logPageClickBi(
      { page_name: PAGE, button_name: 'open_job' },
      job.id ? { entity: { id: job.id, type: ENTITY_TYPE.task } } : undefined,
    );
    openModal(<TaskDetailModal taskId={job.id} />, job.id);
  };

  const fling = (front: number, result: Exclude<SwipeResult, 'none'>) => {
    const dir = result === 'forward' ? 1 : -1;
    setExiting({ deckIndex: front, dir, startX: drag, launched: false });
    setViewed((prev) => new Set(prev).add(front));
    setOrder((prev) => reorderAfterSwipe(prev, result)); // the rest spring forward; the old front → back
    setDrag(0);
    setDragging(false);
    // Launch the fly-off on the next frame (so the start pose renders first), then retire the overlay.
    requestAnimationFrame(() => setExiting((e) => (e ? { ...e, launched: true } : e)));
    exitTimer.current = setTimeout(() => setExiting(null), EXIT_MS);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!multi || exiting) return;
    startXRef.current = e.clientX;
    movedRef.current = false;
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > TAP_SLOP) movedRef.current = true;
    setDrag(dx);
  };
  const endDrag = () => {
    if (!dragging) return;
    const result = commitSwipe(drag, SWIPE_THRESHOLD);
    if (result === 'none') {
      setDrag(0);
      setDragging(false);
      return;
    }
    fling(order[0], result);
  };

  const { countLead, label } = piledUpHeaderParts(
    deckCount,
    `${t(deckCount === 1 ? 'piledUp.summary.jobsOne' : 'piledUp.summary.jobsOther', { count: deckCount })} ${t(
      deckCount === 1 ? 'piledUp.summary.morningOne' : 'piledUp.summary.morning',
    )}`,
  );

  return (
    <section className={styles.section} aria-labelledby="piled-up-heading">
      <Typography
        variant="h4"
        component="h2"
        id="piled-up-heading"
        color="primary"
        className={styles.title}
      >
        {countLead && <span className={styles.count}>{countLead}</span>}
        {countLead ? ` ${label}` : label}
      </Typography>

      <div className={styles.deck} data-static={!multi || undefined}>
        {jobs.map((job, deckIndex) => {
          const p = order.indexOf(deckIndex);
          const isFront = p === 0 && !exiting;
          const hiddenForExit = exiting?.deckIndex === deckIndex; // held invisible under the fly-off overlay
          return (
            <div
              key={job.id}
              className={styles.slot}
              data-front={isFront || undefined}
              data-dragging={isFront && dragging ? true : undefined}
              style={{
                left: (vb - p) * PEEK,
                zIndex: deckCount - p,
                opacity: hiddenForExit ? 0 : restOpacity(p),
                boxShadow: p <= 1 && !hiddenForExit ? 'var(--piled-depth-shadow)' : 'none',
                transform: isFront && drag ? `translateX(${drag}px) rotate(${drag * 0.03}deg)` : undefined,
                ...(deal ? ({ '--deal-delay': `${Math.max(0, vb - p) * 80}ms` } as React.CSSProperties) : {}),
              }}
              data-deal={deal || undefined}
              onPointerDown={isFront ? onPointerDown : undefined}
              onPointerMove={isFront ? onPointerMove : undefined}
              onPointerUp={isFront ? endDrag : undefined}
              onPointerCancel={isFront ? endDrag : undefined}
            >
              <DeckCard
                job={job}
                interactive={isFront}
                onOpen={() => {
                  if (movedRef.current) return; // a swipe, not a tap
                  openJob(job);
                }}
              />
            </div>
          );
        })}

        {exiting && (
          <div
            className={styles.slot}
            data-exit={exiting.launched ? 'go' : 'start'}
            style={
              {
                left: vb * PEEK,
                zIndex: deckCount + 1,
                '--exit-start': `${exiting.startX}px`,
                '--exit-dir': String(exiting.dir),
              } as React.CSSProperties
            }
          >
            <DeckCard job={jobs[exiting.deckIndex]} interactive={false} />
          </div>
        )}
      </div>

      {multi && (
        <div className={styles.dots} aria-hidden>
          {jobs.map((job, deckIndex) => (
            <span key={job.id} className={styles.dot} data-state={dotState(deckIndex, order, viewed)} />
          ))}
        </div>
      )}
    </section>
  );
}

function DeckCard({
  job,
  interactive,
  onOpen,
}: {
  job: PiledUpJob;
  interactive: boolean;
  onOpen?: () => void;
}) {
  const meta = job.agentId ? `${job.agentName} · ${job.workspaceName}` : job.workspaceName;
  return (
    <Card
      variant="sunken"
      size="sm"
      disablePadding
      interactive={interactive}
      component={interactive ? 'button' : 'div'}
      onClick={interactive ? onOpen : undefined}
      aria-hidden={!interactive || undefined}
      className={styles.card}
    >
      <span className={styles.avatar}>
        <PiledUpAvatar job={job} size={68} />
      </span>
      <span className={styles.body}>
        <span className={styles.meta}>{meta}</span>
        <span className={styles.cardTitle}>{job.title}</span>
      </span>
      <CircleCheckBig size={16} aria-hidden className={styles.check} />
    </Card>
  );
}
